import { useEffect, useState } from 'react';

import { api } from '../../api';
import { useLoad } from '../../data';
import { InkHero, Loading, PageHead, Panel, formatDay, zoneDate } from '../../ui';
import { Account, AvailabilityPattern } from '../../types';

const DAYS: { n: number; l: string }[] = [
  { n: 1, l: 'Mon' }, { n: 2, l: 'Tue' }, { n: 3, l: 'Wed' }, { n: 4, l: 'Thu' },
  { n: 5, l: 'Fri' }, { n: 6, l: 'Sat' }, { n: 7, l: 'Sun' },
];
const MONTHS = ['—', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// The recurring working pattern (canon 11 step 5) — the standing shape around
// the day blocks. A day outside it reads unavailable to a hotel, and it scopes
// what Eva may accept. Written through the new PUT /v1/availability/pattern.
function WorkingPattern() {
  const { data, reload } = useLoad<{ pattern: AvailabilityPattern | null }>('/availability/pattern');
  const [seeded, setSeeded] = useState(false);
  const [days, setDays] = useState<number[]>([]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [fromM, setFromM] = useState(0);
  const [toM, setToM] = useState(0);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (data && !seeded) {
      const p = data.pattern;
      if (p) {
        setDays(p.days);
        setStartTime(p.startTime ?? '');
        setEndTime(p.endTime ?? '');
        setFromM(p.seasonStartMonth ?? 0);
        setToM(p.seasonEndMonth ?? 0);
      }
      setSeeded(true);
    }
  }, [data, seeded]);

  const toggleDay = (n: number) => { setDays((d) => (d.includes(n) ? d.filter((x) => x !== n) : [...d, n].sort((a, b) => a - b))); setMsg(''); };
  const hoursPaired = (!startTime && !endTime) || (!!startTime && !!endTime);
  const seasonPaired = (fromM === 0 && toM === 0) || (fromM > 0 && toM > 0);
  const canSave = days.length > 0 && hoursPaired && seasonPaired && (!startTime || !endTime || startTime < endTime);

  const save = async () => {
    if (!canSave) { setErr(days.length === 0 ? 'Pick at least one working day.' : 'Give both hours (start before end) and both season months, or neither.'); return; }
    setBusy(true); setErr(''); setMsg('');
    const body: Record<string, unknown> = { days };
    if (startTime && endTime) { body.startTime = startTime; body.endTime = endTime; }
    if (fromM > 0 && toM > 0) { body.seasonStartMonth = fromM; body.seasonEndMonth = toM; }
    try {
      await api('PUT', '/availability/pattern', body);
      await reload();
      setMsg('Working pattern saved.');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Working pattern" meta={data?.pattern ? 'set' : 'not set'}>
      <div className="field">
        <label>Working days</label>
        <div>
          {DAYS.map((d) => (
            <button key={d.n} type="button" className={`reason ${days.includes(d.n) ? 'selected' : ''}`} onClick={() => toggleDay(d.n)}>
              {d.l}
              {days.includes(d.n) && <span className="disc" />}
            </button>
          ))}
        </div>
      </div>

      <div className="row" style={{ marginTop: 12 }}>
        <div className="field">
          <label htmlFor="pstart">Usual hours from (optional)</label>
          <input id="pstart" type="time" className="mono" value={startTime} onChange={(e) => { setStartTime(e.target.value); setMsg(''); }} />
        </div>
        <div className="field">
          <label htmlFor="pend">to</label>
          <input id="pend" type="time" className="mono" value={endTime} onChange={(e) => { setEndTime(e.target.value); setMsg(''); }} />
        </div>
      </div>

      <div className="row" style={{ marginTop: 12 }}>
        <div className="field">
          <label htmlFor="sfrom">Season from (optional)</label>
          <select id="sfrom" value={fromM} onChange={(e) => { setFromM(Number(e.target.value)); setMsg(''); }}>
            {MONTHS.map((m, i) => (<option key={i} value={i}>{m}</option>))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="sto">to</label>
          <select id="sto" value={toM} onChange={(e) => { setToM(Number(e.target.value)); setMsg(''); }}>
            {MONTHS.map((m, i) => (<option key={i} value={i}>{m}</option>))}
          </select>
        </div>
      </div>

      <button className="btn" onClick={save} disabled={busy || !canSave} style={{ marginTop: 12 }}>
        {busy ? 'Saving…' : 'Save working pattern'}
      </button>
      {msg && <p className="hint" style={{ marginTop: 10 }} role="status">{msg}</p>}
      {err && <p className="error-line" style={{ marginTop: 10 }} role="alert">{err}</p>}
    </Panel>
  );
}

// Availability (canon 09 supplier "Availability" · S4 on the desk). The
// supplier's own calendar of blocked days — weather, maintenance, a day off,
// fully-booked-elsewhere — made unbookable everywhere at once. Availability is
// the supplier's, never the hotel's (canon 13); a hotel only ever learns a day
// is blocked, never why (Eva relays the reason). Reinforces the block API the
// PWA already drives (GET /v1/availability + block/unblock) — no new endpoint.
// A range is the canon 11 F1 "block a range" for maintenance, built as a loop
// over the one-day endpoint, not a second one.

const BLOCK_REASONS: { code: string; label: string }[] = [
  { code: 'weather', label: 'Weather' },
  { code: 'maintenance', label: 'Maintenance' },
  { code: 'day_off', label: 'Day off' },
  { code: 'fully_booked', label: 'Fully booked elsewhere' },
  { code: 'other', label: 'Something else…' },
];

interface Block {
  date: string;
  reason: string;
  note?: string;
  createdAt?: string;
}

// The days from `from` to `to` inclusive, as YYYY-MM-DD. Parsed at UTC noon so
// the day never rolls over an offset.
function daysInRange(from: string, to: string): string[] {
  const out: string[] = [];
  let t = Date.parse(`${from}T12:00:00Z`);
  const end = Date.parse(`${to}T12:00:00Z`);
  for (let i = 0; t <= end && i < 366; i++, t += 86_400_000) {
    out.push(new Date(t).toISOString().slice(0, 10));
  }
  return out;
}

export function Availability() {
  const account = useLoad<Account>('/account');
  const tz = account.data?.timezone ?? 'Europe/Athens';
  const today = zoneDate(tz, Date.now());
  const horizon = zoneDate(tz, Date.now() + 180 * 86_400_000);
  const { data, error, reload } = useLoad<{ blocks: Block[] }>(`/availability?from=${today}&to=${horizon}`);

  const [from, setFrom] = useState(today);
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgKind, setMsgKind] = useState<'ok' | 'warn'>('ok');

  const blocks = (data?.blocks ?? []).slice().sort((a, b) => a.date.localeCompare(b.date));
  const rangeEnd = to && to > from ? to : from;
  const dayCount = from ? daysInRange(from, rangeEnd).length : 0;

  const block = async () => {
    if (!from || !reason) return;
    setBusy(true);
    setMsg('');
    const days = daysInRange(from, rangeEnd);
    const clashes: string[] = [];
    for (const d of days) {
      try {
        await api('POST', `/availability/${d}/block`, { reason, note: note || undefined });
      } catch (e) {
        // 409: a confirmed job that day — it cannot be silently blocked, it
        // must be cancelled first (which tells the hotel). Skip it, name it.
        clashes.push(d);
        void e;
      }
    }
    setBusy(false);
    setNote('');
    reload();
    if (clashes.length === 0) {
      setMsgKind('ok');
      setMsg(days.length === 1 ? 'Day blocked.' : `${days.length} days blocked.`);
    } else {
      setMsgKind('warn');
      setMsg(
        `Blocked ${days.length - clashes.length} of ${days.length}. You have a confirmed job on ${clashes
          .map(formatDay)
          .join(', ')} — cancel it first (the hotel is told), then block the day.`,
      );
    }
  };

  const unblock = async (date: string) => {
    await api('POST', `/availability/${date}/unblock`);
    reload();
  };

  const reasonLabel = (code: string) => BLOCK_REASONS.find((r) => r.code === code)?.label ?? code;

  return (
    <div>
      <PageHead title="Availability" sub="Make a day unbookable — a hotel only ever sees that it's blocked, never why." />

      <InkHero
        eyebrow="Blocked ahead"
        figure={data ? blocks.length : '…'}
        explain={blocks.length ? `${blocks.length} day${blocks.length === 1 ? '' : 's'} closed in the next 180` : 'Every day is open to requests'}
      />

      <WorkingPattern />

      <Panel title="Block days">
        <div className="row">
          <div className="field">
            <label htmlFor="from">From</label>
            <input id="from" type="date" className="mono" min={today} value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="to">To (optional)</label>
            <input id="to" type="date" className="mono" min={from || today} value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>

        <div className="field" style={{ marginTop: 12 }}>
          <label>Why</label>
          <div>
            {BLOCK_REASONS.map((r) => (
              <button
                key={r.code}
                type="button"
                className={`reason ${reason === r.code ? 'selected' : ''} ${r.code === 'other' ? 'muted' : ''}`}
                onClick={() => setReason(r.code)}
                aria-pressed={reason === r.code}
              >
                {r.label}
                {reason === r.code && <span className="disc" />}
              </button>
            ))}
          </div>
        </div>

        {reason === 'other' && (
          <div className="field" style={{ marginTop: 12 }}>
            <label htmlFor="bnote">A note (for you and Eva)</label>
            <input id="bnote" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        )}

        <button className="btn" disabled={busy || !from || !reason} onClick={block} style={{ marginTop: 12 }}>
          {busy ? 'Blocking…' : dayCount > 1 ? `Block ${dayCount} days` : 'Block this day'}
        </button>
        {msg && <p className={msgKind === 'warn' ? 'error-line' : 'hint'} style={{ marginTop: 10 }} role="status">{msg}</p>}
      </Panel>

      <Panel title="Blocked days" meta={data ? `${blocks.length} ahead` : ''} flush>
        {error && !data ? (
          <div className="card-body"><p className="error-line">{error}</p></div>
        ) : !data ? (
          <div className="card-body"><Loading /></div>
        ) : blocks.length === 0 ? (
          <div className="card-body"><div className="empty-neutral">Nothing blocked — every day is open to requests.</div></div>
        ) : (
          <table>
            <tbody>
              {blocks.map((b) => (
                <tr key={b.date}>
                  <td>{formatDay(b.date)}</td>
                  <td className="hint">
                    {reasonLabel(b.reason)}
                    {b.note ? ` · ${b.note}` : ''}
                  </td>
                  <td className="num">
                    <button className="btn ghost" onClick={() => unblock(b.date)}>Open up</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
